// src/systems/spin.ts
//
// Phase 6 — slot picker that wraps the wheel candidate pool with BallEffect bias.
// Pure function: side effects limited to consuming the RNG. The candidate pool is
// rebuilt fresh on each call so weight/colour/parity bias is purely additive
// (extra slot copies pushed onto the array). `forceNumber` short-circuits to the
// named slot regardless of RNG; `rerollOnZero` re-rolls exactly once if the first
// pick is the green zero.
//
// Phase 13 (BETS-09, WHEEL-08): migrated to slot.kind === 'number' narrowing.
// - buildWeightedCandidates filters to number-slots before pushing biased copies,
//   so bias balls can NEVER increase the probability of landing on a void slot.
// - forceNumber finds via s.kind === 'number' && s.value === forced (not s.number).
// - rerollOnZero narrows on pick.kind === 'number' && pick.value === 0 (not pick.number === 0).
// - SpinOpts gains an optional `wheel` parameter (defaults to legacy WHEEL constant).

import type { Ball, BetType, Slot, SlotNumber } from '../state/types';
import type { Rng } from './rng';
import { WHEEL, RED_NUMBERS } from './wheel';

export interface SpinOpts {
  ball: Ball | null;
  rng: Rng;
  /** All current placements — used by Magnet Ball to find the dominant color. */
  placements: Array<{ bet: BetType }>;
  /**
   * Phase 13 (WHEEL-08): the composed wheel for this run. If omitted,
   * defaults to the legacy frozen `WHEEL` constant (all kind:'number'
   * slots). Plan 13-04 wires GameState to pass `gs.getState().wheel`.
   */
  wheel?: readonly Slot[];
  /**
   * Phase 15 (CONSUMABLE-06): loaded_ball consumable sets RunState.forcedNumber.
   * When non-null, overrides ball.effect.forceNumber. Cleared after spin by
   * RoundScene via gs.setForcedNumber(null) (plan 15-07).
   */
  forcedNumber?: SlotNumber | null;
}

/** Pick a slot for this spin, applying ball-effect bias. */
export function pickSlotForSpin(opts: SpinOpts): Slot {
  const { ball, rng, placements, wheel = WHEEL, forcedNumber } = opts;

  // Phase 15 (CONSUMABLE-06): RunState-level forcedNumber takes priority over
  // ball.effect.forceNumber. Set by loaded_ball consumable; cleared by
  // RoundScene after spin resolves (plan 15-07).
  if (forcedNumber != null) {
    return (
      wheel.find((s) => s.kind === 'number' && s.value === forcedNumber) ??
      wheel.find((s) => s.kind === 'number') ??
      wheel[0]!
    );
  }

  // forceNumber short-circuits before any RNG consumption.
  // Find the FIRST number-slot whose value matches.
  // If the forced number has been removed from the deck (Phase 15 consumables),
  // fall back to the first number-slot in the wheel rather than positional
  // wheel[0] which may be a void slot — yielding a confusing silent loss
  // (WR-02 fix). Absolute last resort (all-void wheel) still uses wheel[0].
  if (ball?.effect?.type === 'forceNumber') {
    const forced = ball.effect.number;
    return (
      wheel.find((s) => s.kind === 'number' && s.value === forced) ??
      wheel.find((s) => s.kind === 'number') ?? // first number slot as fallback
      wheel[0]!                                  // degenerate all-void wheel — accepted
    );
  }

  const candidates = buildWeightedCandidates(ball, placements, wheel);
  let pick = candidates[Math.floor(rng() * candidates.length)]!;

  // Lucky Ball: rerollOnZero — fires ONLY on number-zero, NOT on void
  // (per BETS-09 + 13-CONTEXT.md "Bias balls iterate slot.kind === 'number' only").
  // Void slots produce a full loss at the bet-resolution layer (isLosingSlot).
  if (
    pick.kind === 'number' &&
    pick.value === 0 &&
    ball?.effect?.type === 'rerollOnZero'
  ) {
    pick = candidates[Math.floor(rng() * candidates.length)]!;
  }

  return pick;
}

function buildWeightedCandidates(
  ball: Ball | null,
  placements: Array<{ bet: BetType }>,
  wheel: readonly Slot[],
): Slot[] {
  // Base candidate pool = the full wheel (voids + numbers — voids may still
  // be picked uniformly by a non-bias ball, which correctly produces a loss).
  const arr: Slot[] = [...wheel];

  if (!ball || !ball.effect) return arr;

  const eff = ball.effect;

  // The bias arm operates on number-slots ONLY. We never push a void slot
  // as an "extra" weight, so bias balls cannot increase the probability of
  // landing on void (T-13-07).
  const numberSlots = wheel.filter(
    (s): s is Extract<Slot, { kind: 'number' }> => s.kind === 'number',
  );

  if (eff.type === 'biasColor') {
    const targetColor = eff.color;
    const weight = Math.max(1, Math.floor(eff.weight));
    for (let extra = 1; extra < weight; extra++) {
      numberSlots.forEach((s) => {
        if (s.color === targetColor) arr.push(s);
      });
    }
  } else if (eff.type === 'biasParity') {
    const wantEven = eff.parity === 'even';
    const weight = Math.max(1, Math.floor(eff.weight));
    for (let extra = 1; extra < weight; extra++) {
      numberSlots.forEach((s) => {
        if (s.value === 0) return; // 0 is neither even nor odd
        const isEven = s.value % 2 === 0;
        if (isEven === wantEven) arr.push(s);
      });
    }
  } else if (eff.type === 'biasMagnet') {
    let red = 0;
    let black = 0;
    placements.forEach(({ bet }) => {
      if (bet.kind === 'red') red++;
      else if (bet.kind === 'black') black++;
      else if (bet.kind === 'straight') {
        if (bet.number !== 0) {
          if (RED_NUMBERS.has(bet.number)) red++;
          else black++;
        }
      }
    });
    const target: 'red' | 'black' | null =
      red > black ? 'red' : black > red ? 'black' : null;
    if (target) {
      const weight = Math.max(1, Math.floor(eff.weight));
      for (let extra = 1; extra < weight; extra++) {
        numberSlots.forEach((s) => {
          if (s.color === target) arr.push(s);
        });
      }
    }
  }

  return arr;
}
